import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  ArrowLeft, HelpCircle, MessageSquare, Trophy, Wallet, Gift,
  RefreshCw, Inbox, Check, Send, Truck, X, Lock,
} from 'lucide-react';

const BUCKETS = [
  { key: 'questions', label: 'Questions', icon: HelpCircle, color: 'text-pink-500' },
  { key: 'messages', label: 'Messages', icon: MessageSquare, color: 'text-violet-500' },
  { key: 'victories', label: 'Victoires', icon: Trophy, color: 'text-yellow-500' },
  { key: 'payouts', label: 'Virements', icon: Wallet, color: 'text-green-600' },
  { key: 'rewards', label: 'Récompenses', icon: Gift, color: 'text-primary' },
];

const initials = (name = '') =>
  name.split(' ').map((p) => p[0]).filter(Boolean).slice(0, 2).join('').toUpperCase() || 'M';

const PasserellePage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [denied, setDenied] = useState(false);

  const fetchData = async () => {
    try {
      const res = await api.get('/admin/passerelle');
      setData(res.data);
      setDenied(false);
    } catch (e) {
      if (e.response?.status === 403) {
        setDenied(true);
      } else {
        toast.error("Impossible de charger la Passerelle");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const act = async (id, fn, successMessage) => {
    setBusy(id);
    try {
      await fn();
      toast.success(successMessage);
      await fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action impossible');
    } finally {
      setBusy(null);
    }
  };

  const counts = data?.counts || {};
  const buckets = data?.buckets || {};

  const Row = ({ testId, author, avatar, title, meta, children }) => (
    <Card className="border-border" data-testid={testId}>
      <CardContent className="p-4 flex items-start gap-3 flex-wrap sm:flex-nowrap">
        <Avatar className="w-9 h-9 shrink-0">
          <AvatarImage src={avatar} />
          <AvatarFallback className="text-xs">{initials(author)}</AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium break-words">{title}</p>
          <p className="text-xs text-muted-foreground mt-0.5 break-words">
            {author}
            {meta ? ` · ${meta}` : ''}
          </p>
        </div>
        <div className="flex gap-2 shrink-0 ml-auto">{children}</div>
      </CardContent>
    </Card>
  );

  const Empty = ({ label }) => (
    <div className="text-center py-10 text-muted-foreground" data-testid="passerelle-empty">
      <Check className="w-8 h-8 mx-auto mb-2 text-green-500" />
      <p className="text-sm">Rien à traiter : {label} à jour.</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-background" data-testid="passerelle-page">
      <header className="border-b border-border bg-card/70 backdrop-blur-xl sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3 sm:py-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <Button variant="ghost" size="icon" onClick={() => navigate('/')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="w-10 h-10 rounded-xl bg-primary/15 flex items-center justify-center shrink-0">
              <Inbox className="w-5 h-5 text-primary" />
            </div>
            <div className="min-w-0">
              <h1 className="font-heading font-bold text-base sm:text-xl truncate">Passerelle du Capitaine</h1>
              <p className="text-xs sm:text-sm text-muted-foreground">
                {counts.total || 0} élément{(counts.total || 0) > 1 ? 's' : ''} en attente
              </p>
            </div>
          </div>
          <Button variant="outline" size="icon" onClick={fetchData} data-testid="passerelle-refresh">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6">
        {loading ? (
          <div className="space-y-3">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-20 rounded-xl bg-secondary/50 animate-pulse" />
            ))}
          </div>
        ) : denied ? (
          <div className="text-center py-16" data-testid="passerelle-denied">
            <Lock className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
            <h2 className="font-heading font-semibold text-lg mb-1">Accès réservé à l&apos;équipage de commandement</h2>
            <p className="text-sm text-muted-foreground mb-5">
              Seuls l&apos;amiral, les majors et les animateurs de communauté accèdent à la Passerelle.
            </p>
            <Button onClick={() => navigate('/')} data-testid="passerelle-denied-home">
              Retour à l&apos;accueil
            </Button>
          </div>
        ) : (
          <Tabs defaultValue="questions">
            <TabsList className="bg-secondary/50 w-full justify-start overflow-x-auto mb-5">
              {BUCKETS.map((b) => {
                const Icon = b.icon;
                return (
                  <TabsTrigger
                    key={b.key}
                    value={b.key}
                    className="shrink-0 text-xs sm:text-sm flex items-center gap-1.5"
                    data-testid={`passerelle-tab-${b.key}`}
                  >
                    <Icon className={`w-4 h-4 ${b.color}`} />
                    {b.label}
                    {counts[b.key] > 0 && (
                      <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-[10px]">
                        {counts[b.key]}
                      </Badge>
                    )}
                  </TabsTrigger>
                );
              })}
            </TabsList>

            {/* Questions sans réponse */}
            <TabsContent value="questions" className="space-y-3">
              {(buckets.questions || []).length === 0 ? (
                <Empty label="toutes les questions sont traitées" />
              ) : (
                buckets.questions.map((q) => (
                  <Row
                    key={q.id}
                    testId={`passerelle-question-${q.id}`}
                    author={q.author_name}
                    avatar={q.author_avatar}
                    title={q.question}
                    meta={`${q.chapter_title || 'Question générale'} · ${q.votes} vote(s)`}
                  >
                    <Button size="sm" onClick={() => navigate('/questions')} data-testid={`passerelle-answer-${q.id}`}>
                      <Send className="w-4 h-4 mr-1" />
                      Répondre
                    </Button>
                  </Row>
                ))
              )}
            </TabsContent>

            {/* Messages privés non lus */}
            <TabsContent value="messages" className="space-y-3">
              {(buckets.messages || []).length === 0 ? (
                <Empty label="aucun message privé non lu"/>
              ) : (
                buckets.messages.map((m) => (
                  <Row
                    key={m.user_id}
                    testId={`passerelle-dm-${m.user_id}`}
                    author={m.author_name}
                    avatar={m.author_avatar}
                    title={m.last_message}
                    meta={`${m.count} message(s) non lu(s)`}
                  >
                    <Button size="sm" onClick={() => navigate(`/dm/${m.user_id}`)} data-testid={`passerelle-open-dm-${m.user_id}`}>
                      <MessageSquare className="w-4 h-4 mr-1" />
                      Ouvrir
                    </Button>
                  </Row>
                ))
              )}
            </TabsContent>

            {/* Victoires à féliciter */}
            <TabsContent value="victories" className="space-y-3">
              {(buckets.victories || []).length === 0 ? (
                <Empty label="toutes les victoires sont félicitées" />
              ) : (
                buckets.victories.map((v) => (
                  <Row
                    key={v.id}
                    testId={`passerelle-victory-${v.id}`}
                    author={v.author_name}
                    avatar={v.author_avatar}
                    title={v.title}
                    meta={v.description}
                  >
                    <Button
                      size="sm"
                      className="bg-yellow-500 text-black hover:bg-yellow-400"
                      disabled={busy === v.id}
                      onClick={() => act(v.id, () => api.post(`/victories/${v.id}/congratulate`), 'Félicitations envoyées')}
                      data-testid={`passerelle-congratulate-${v.id}`}
                    >
                      <Trophy className="w-4 h-4 mr-1" />
                      Féliciter
                    </Button>
                  </Row>
                ))
              )}
            </TabsContent>

            {/* Virements ambassadeur */}
            <TabsContent value="payouts" className="space-y-3">
              {(buckets.payouts || []).length === 0 ? (
                <Empty label="aucune demande de virement" />
              ) : (
                buckets.payouts.map((p) => (
                  <Row
                    key={p.id}
                    testId={`passerelle-payout-${p.id}`}
                    author={p.author_name}
                    avatar={p.author_avatar}
                    title={`${p.amount} € à verser`}
                    meta={`${p.method || 'virement'}${p.details ? ` · ${p.details}` : ''}`}
                  >
                    <Button
                      size="sm"
                      className="bg-green-600 hover:bg-green-700"
                      disabled={busy === p.id}
                      onClick={() => act(p.id, () => api.post(`/admin/ambassador/payouts/${p.id}/pay`), 'Virement marqué payé')}
                      data-testid={`passerelle-pay-${p.id}`}
                    >
                      <Check className="w-4 h-4 mr-1" />
                      Payé
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 border-red-500/40 hover:bg-red-500/10"
                      disabled={busy === p.id}
                      onClick={() => act(p.id, () => api.post(`/admin/ambassador/payouts/${p.id}/reject`), 'Demande refusée')}
                      data-testid={`passerelle-reject-${p.id}`}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </Row>
                ))
              )}
            </TabsContent>

            {/* Récompenses à expédier */}
            <TabsContent value="rewards" className="space-y-3">
              {(buckets.rewards || []).length === 0 ? (
                <Empty label="aucune récompense à expédier" />
              ) : (
                buckets.rewards.map((c) => (
                  <Row
                    key={c.id}
                    testId={`passerelle-reward-${c.id}`}
                    author={c.author_name}
                    avatar={c.author_avatar}
                    title={c.reward_title || `Récompense niveau ${c.level}`}
                    meta={c.shipping_address || 'Adresse non renseignée'}
                  >
                    <Button
                      size="sm"
                      disabled={busy === c.id}
                      onClick={() => act(c.id, () => api.put(`/admin/reward-claims/${c.id}/fulfill`), 'Récompense marquée expédiée')}
                      data-testid={`passerelle-fulfill-${c.id}`}
                    >
                      <Truck className="w-4 h-4 mr-1" />
                      Expédiée
                    </Button>
                  </Row>
                ))
              )}
            </TabsContent>
          </Tabs>
        )}
      </main>
    </div>
  );
};

export default PasserellePage;
